import { useEffect, useState, useCallback } from "react";
import { UgcQueueSettingsCard } from "../components/admin/UgcQueueSettingsCard";
import { ActionSuccessReportCard } from "../components/admin/ActionSuccessReportCard";
import { StorageWarningBanner } from "../components/admin/StorageWarningBanner";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { adminAPI, getAssetUrl, BASE_URL, getVideoUrl } from "../lib/api";
import { formatNumber } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Users,
  Film,
  HardDrive,
  TrendingUp,
  Shield,
  ShieldCheck,
  ShieldOff,
  Search,
  MoreVertical,
  Loader2,
  Calendar,
  Activity,
  Eye,
  Trash2,
  UserCheck,
  UserX,
  Crown,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Gift,
  DollarSign,
  Wallet,
  PieChart,
  TrendingDown,
  BarChart3,
  Percent,
  CreditCard,
  HandCoins,
  Coins,
  User,
  ExternalLink,
  Star,
  StarOff,
  ChevronRight,
  CheckSquare,
  Copy,
  X,
  Download,
  Headphones,
  MessageSquare,
  Send,
} from "lucide-react";
import UserProfileModal from "../components/admin/UserProfileModal";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const statusColors = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  approved: "bg-green-500/20 text-green-400 border-green-500/30",
  generating: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  published: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  failed: "bg-red-500/20 text-red-400 border-red-500/30",
  rejected: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  generating: RefreshCw,
  published: CheckCircle,
  failed: XCircle,
  rejected: XCircle,
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [videoFilter, setVideoFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(true);
  
  // Video viewer state
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  
  // User videos lookup state
  const [userVideosOpen, setUserVideosOpen] = useState(false);
  
  // Activity Logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogStats, setActivityLogStats] = useState(null);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogFilter, setActivityLogFilter] = useState("all");
  const [activitySeverityFilter, setActivitySeverityFilter] = useState("all");
  const [showResolvedLogs, setShowResolvedLogs] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  const [userVideosData, setUserVideosData] = useState(null);
  const [userVideosLoading, setUserVideosLoading] = useState(false);
  const [userEmailSearch, setUserEmailSearch] = useState("");
  
  // User profile modal state
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [userProfileData, setUserProfileData] = useState(null);
  
  // Featured videos state
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuringVideoId, setFeaturingVideoId] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  
  // Referral admin state
  const [referralStats, setReferralStats] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);
  
  // Manual referral assignment state
  const [assignReferralEmail, setAssignReferralEmail] = useState("");
  const [assignReferralCode, setAssignReferralCode] = useState("");
  const [assignReferralLoading, setAssignReferralLoading] = useState(false);
  const [usersWithoutReferrer, setUsersWithoutReferrer] = useState([]);
  const [searchUnaffiliatedUsers, setSearchUnaffiliatedUsers] = useState("");
  
  // Referrer management state (NEW)
  const [allReferrers, setAllReferrers] = useState([]);
  const [referrersLoading, setReferrersLoading] = useState(false);
  const [referrerSearch, setReferrerSearch] = useState("");
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referrerDetails, setReferrerDetails] = useState(null);
  const [referrerDetailsLoading, setReferrerDetailsLoading] = useState(false);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  
  // Bulk assignment state (NEW)
  const [selectedUsersForBulk, setSelectedUsersForBulk] = useState([]);
  const [bulkReferrerCode, setBulkReferrerCode] = useState("");
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  
  // Referral view filter state (NEW)
  const [referralViewFilter, setReferralViewFilter] = useState("overview"); // overview, referrers, unassigned
  
  // Financials Dashboard state
  const [financialsOverview, setFinancialsOverview] = useState(null);
  const [revenueChartData, setRevenueChartData] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [chartPeriod, setChartPeriod] = useState("30d");
  
  // Subscription Management state (NEW)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionEmail, setSubscriptionEmail] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("premium");
  const [subscriptionDuration, setSubscriptionDuration] = useState(1);
  const [subscriptionPaymentMethod, setSubscriptionPaymentMethod] = useState("bank_transfer");
  const [subscriptionNotes, setSubscriptionNotes] = useState("");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [manualSubscriptions, setManualSubscriptions] = useState([]);
  
  // User search autocomplete state (NEW)
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  
  // User action modal state (NEW)
  const [showUserActionModal, setShowUserActionModal] = useState(false);
  const [userActionType, setUserActionType] = useState(""); // "subscription", "trial", "credits"
  
  // Active tab state
  const [activeTab, setActiveTab] = useState("users");
  
  // Support Tickets state (NEW)
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportFilter, setSupportFilter] = useState({ status: "", priority: "", category: "" });
  const [supportSearch, setSupportSearch] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketReplyLoading, setTicketReplyLoading] = useState(false);
  const [replyTemplates, setReplyTemplates] = useState([]);
  const [ticketUpdateLoading, setTicketUpdateLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
        toast.error("Admin access required");
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers(0, 100, searchQuery || undefined);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, [searchQuery]);

  const fetchVideos = useCallback(async () => {
    try {
      const response = await adminAPI.getVideos(0, 100, videoFilter === "all" ? undefined : videoFilter);
      setVideos(response.data);
    } catch (error) {
      console.error("Failed to fetch videos:", error);
    }
  }, [videoFilter]);

  // Fetch featured videos
  const fetchFeaturedVideos = useCallback(async () => {
    setFeaturedLoading(true);
    try {
      const response = await adminAPI.getFeaturedVideos();
      setFeaturedVideos(response.data?.featured_videos || []);
    } catch (error) {
      console.error("Failed to fetch featured videos:", error);
      toast.error("Failed to load featured videos");
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  // Toggle feature status of a video
  const handleToggleFeature = async (videoId, currentlyFeatured) => {
    if (!videoId) {
      toast.error("Video ID is missing");
      return;
    }
    
    setFeaturingVideoId(videoId);
    try {
      const response = await adminAPI.toggleFeatureVideo(videoId);
      toast.success(response.data.message);
      
      // Update local state
      if (response.data.is_featured) {
        // Refetch featured videos to get the updated list
        fetchFeaturedVideos();
      } else {
        // Remove from featured list
        setFeaturedVideos(prev => prev.filter(v => v.id !== videoId));
      }
      
      // Update videos list
      setVideos(prev => prev.map(v => 
        (v.id === videoId || v._id === videoId)
          ? { ...v, is_featured: response.data.is_featured } 
          : v
      ));
    } catch (error) {
      console.error("Feature toggle error:", error);
      toast.error(error.response?.data?.detail || "Failed to update feature status");
    } finally {
      setFeaturingVideoId(null);
    }
  };

  const fetchReferralData = useCallback(async () => {
    setReferralLoading(true);
    try {
      const [statsRes, payoutsRes] = await Promise.all([
        adminAPI.getReferralStats(),
        adminAPI.getPendingPayouts(),
      ]);
      setReferralStats(statsRes.data);
      setPendingPayouts(payoutsRes.data.payouts || []);
    } catch (error) {
      console.error("Failed to fetch referral data:", error);
    } finally {
      setReferralLoading(false);
    }
  }, []);

  // Fetch users without referrer for manual assignment
  const fetchUsersWithoutReferrer = useCallback(async (search = "") => {
    try {
      const res = await adminAPI.getUsersWithoutReferrer(50, search || null);
      setUsersWithoutReferrer(res.data.users || []);
    } catch (error) {
      console.error("Failed to fetch users without referrer:", error);
    }
  }, []);

  // Handle manual referral assignment
  const handleAssignReferral = async () => {
    if (!assignReferralEmail || !assignReferralCode) {
      toast.error("Please enter both user email and referrer code");
      return;
    }
    
    setAssignReferralLoading(true);
    try {
      const res = await adminAPI.assignReferral(assignReferralEmail, assignReferralCode);
      toast.success(res.data.message || "Referral assigned successfully");
      setAssignReferralEmail("");
      setAssignReferralCode("");
      fetchReferralData();
      fetchUsersWithoutReferrer(searchUnaffiliatedUsers);
      fetchAllReferrers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign referral");
    } finally {
      setAssignReferralLoading(false);
    }
  };

  // Fetch all referrers (NEW)
  const fetchAllReferrers = useCallback(async (search = "") => {
    setReferrersLoading(true);
    try {
      const res = await adminAPI.getAllReferrers(100, 0, "referral_count", search || null);
      setAllReferrers(res.data.referrers || []);
    } catch (error) {
      console.error("Failed to fetch referrers:", error);
    } finally {
      setReferrersLoading(false);
    }
  }, []);

  // Fetch referrer details (NEW)
  const fetchReferrerDetails = async (userId) => {
    console.log('[REFERRER] fetchReferrerDetails called with userId:', userId);
    
    // Validate userId to prevent undefined/null API calls
    if (!userId) {
      console.error('[REFERRER] ERROR: userId is undefined or null');
      toast.error("Invalid referrer ID");
      return;
    }
    
    setReferrerDetailsLoading(true);
    try {
      console.log('[REFERRER] Calling API for user:', userId);
      const res = await adminAPI.getReferrerDetails(userId);
      console.log('[REFERRER] API response:', res.data);
      setReferrerDetails(res.data);
      setShowReferrerModal(true);
    } catch (error) {
      console.error("[REFERRER] Failed to fetch referrer details:", error);
      console.error("[REFERRER] Error response:", error.response?.data);
      console.error("[REFERRER] Error status:", error.response?.status);
      toast.error("Failed to load referrer details");
    } finally {
      setReferrerDetailsLoading(false);
    }
  };

  // Handle bulk referral assignment (NEW)
  const handleBulkAssignReferrals = async () => {
    if (selectedUsersForBulk.length === 0) {
      toast.error("Please select users to assign");
      return;
    }
    if (!bulkReferrerCode) {
      toast.error("Please enter a referrer code");
      return;
    }
    
    setBulkAssignLoading(true);
    try {
      const assignments = selectedUsersForBulk.map(user => ({
        user_email: user.email,
        referrer_code: bulkReferrerCode
      }));
      
      const res = await adminAPI.bulkAssignReferrals(assignments);
      
      if (res.data.successful > 0) {
        toast.success(`Successfully assigned ${res.data.successful} users`);
      }
      if (res.data.failed > 0) {
        toast.error(`Failed to assign ${res.data.failed} users`);
      }
      
      setSelectedUsersForBulk([]);
      setBulkReferrerCode("");
      fetchReferralData();
      fetchUsersWithoutReferrer(searchUnaffiliatedUsers);
      fetchAllReferrers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk assignment failed");
    } finally {
      setBulkAssignLoading(false);
    }
  };

  // Toggle user selection for bulk assignment (NEW)
  const toggleUserSelection = (user) => {
    setSelectedUsersForBulk(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  // Select all unassigned users (NEW)
  const selectAllUnassigned = () => {
    setSelectedUsersForBulk(usersWithoutReferrer);
  };

  // Clear selection (NEW)
  const clearSelection = () => {
    setSelectedUsersForBulk([]);
  };

  // Fetch manual subscriptions
  const fetchManualSubscriptions = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/admin/subscription/manual-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setManualSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error("Failed to fetch manual subscriptions:", error);
    }
  }, []);

  // Assign subscription to user
  const handleAssignSubscription = async () => {
    const emailToUse = selectedUserForAction?.email || subscriptionEmail;
    if (!emailToUse) {
      toast.error("Please enter or select a user");
      return;
    }
    
    setSubscriptionLoading(true);
    try {
      const token = localStorage.getItem("token");
      // Convert months to days
      const durationDays = subscriptionDuration * 30;
      
      const res = await fetch(`${BASE_URL}/api/admin/subscription/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_email: emailToUse,
          plan: subscriptionPlan,
          billing_cycle: "monthly",
          duration_days: durationDays,
          payment_method: subscriptionPaymentMethod,
          notes: subscriptionNotes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Subscription assigned successfully");
        setShowSubscriptionModal(false);
        setShowUserActionModal(false);
        setSubscriptionEmail("");
        setSubscriptionNotes("");
        setSelectedUserForAction(null);
        fetchManualSubscriptions();
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to assign subscription");
      }
    } catch (error) {
      toast.error("Failed to assign subscription");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Search users with debounce for autocomplete
  const searchUsersDebounced = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }
    
    setUserSearchLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/admin/users/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserSearchResults(data.users || []);
        setShowUserDropdown(true);
      }
    } catch (error) {
      console.error("User search failed:", error);
    } finally {
      setUserSearchLoading(false);
    }
  }, []);

  // Handle user selection from autocomplete
  const handleSelectUser = (user) => {
    setSelectedUserForAction(user);
    setSubscriptionEmail(user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  // Open user action modal (subscription/trial/credits)
  const openUserActionModal = (user, actionType) => {
    setSelectedUserForAction(user);
    setSubscriptionEmail(user.email);
    setUserActionType(actionType);
    if (actionType === "subscription") {
      setShowSubscriptionModal(true);
    } else {
      setShowUserActionModal(true);
    }
  };

  // Grant trial to user
  const handleGrantTrial = async (user, days) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/admin/trial/grant-package`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_email: user.email,
          plan: "premium",
          duration_days: days,
          reason: "Admin granted trial"
        })
      });
      
      if (res.ok) {
        toast.success(`${days}-day Premium trial granted to ${user.email}`);
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to grant trial");
      }
    } catch (error) {
      toast.error("Failed to grant trial");
    }
  };

  // Fetch activity logs
  const fetchActivityLogs = useCallback(async () => {
    setActivityLogsLoading(true);
    try {
      const params = {};
      if (activityLogFilter !== "all") params.log_type = activityLogFilter;
      if (activitySeverityFilter !== "all") params.severity = activitySeverityFilter;
      if (!showResolvedLogs) params.resolved = false;
      
      const [logsRes, statsRes] = await Promise.all([
        adminAPI.getActivityLogs(params),
        adminAPI.getActivityLogStats(),
      ]);
      
      setActivityLogs(logsRes.data.logs || []);
      setActivityLogStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
    } finally {
      setActivityLogsLoading(false);
    }
  }, [activityLogFilter, activitySeverityFilter, showResolvedLogs]);

  const handleResolveLog = async (logId) => {
    try {
      await adminAPI.resolveActivityLog(logId);
      toast.success("Log marked as resolved");
      fetchActivityLogs();
    } catch (error) {
      toast.error("Failed to resolve log");
    }
  };

  const handleResolveAllLogs = async (logType = null) => {
    try {
      const res = await adminAPI.resolveAllActivityLogs(logType);
      toast.success(`Resolved ${res.data.resolved_count} logs`);
      fetchActivityLogs();
    } catch (error) {
      toast.error("Failed to resolve logs");
    }
  };

  // Fetch financials data
  const fetchFinancialsData = useCallback(async () => {
    setFinancialsLoading(true);
    try {
      const [overviewRes, chartRes, costsRes, subsRes] = await Promise.all([
        adminAPI.getFinancialsOverview(),
        adminAPI.getRevenueChart(chartPeriod),
        adminAPI.getCostBreakdown(),
        adminAPI.getSubscriptionStats(),
      ]);
      
      setFinancialsOverview(overviewRes.data);
      setRevenueChartData(chartRes.data);
      setCostBreakdown(costsRes.data);
      setSubscriptionStats(subsRes.data);
    } catch (error) {
      console.error("Failed to fetch financials:", error);
      toast.error("Failed to load financial data");
    } finally {
      setFinancialsLoading(false);
    }
  }, [chartPeriod]);

  // Refetch chart data when period changes
  useEffect(() => {
    if (activeTab === "financials" && financialsOverview) {
      adminAPI.getRevenueChart(chartPeriod).then(res => {
        setRevenueChartData(res.data);
      }).catch(console.error);
    }
  }, [chartPeriod, activeTab, financialsOverview]);

  // Fetch support tickets
  const fetchSupportTickets = useCallback(async () => {
    setSupportLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (supportFilter.status) params.append("ticket_status", supportFilter.status);
      if (supportFilter.priority) params.append("priority", supportFilter.priority);
      if (supportFilter.category) params.append("category", supportFilter.category);
      if (supportSearch) params.append("search", supportSearch);
      params.append("limit", "50");
      
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSupportTickets(data.tickets || []);
        setSupportStats(data.stats || null);
      }
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    } finally {
      setSupportLoading(false);
    }
  }, [supportFilter, supportSearch]);

  // Fetch reply templates
  const fetchReplyTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReplyTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Failed to fetch reply templates:", error);
    }
  }, []);

  // Update ticket status/priority
  const handleUpdateTicket = async (ticketId, updates) => {
    setTicketUpdateLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets/${ticketId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success("Ticket updated");
        fetchSupportTickets();
        if (selectedTicket?.id === ticketId) {
          fetchTicketDetails(ticketId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update ticket");
      }
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setTicketUpdateLoading(false);
    }
  };

  // Fetch single ticket details
  const fetchTicketDetails = async (ticketId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      console.error("Failed to fetch ticket details:", error);
    }
  };

  // Reply to ticket
  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    setTicketReplyLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets/${selectedTicket.id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: ticketReply })
      });
      
      if (res.ok) {
        setTicketReply("");
        fetchTicketDetails(selectedTicket.id);
        fetchSupportTickets();
        toast.success("Reply sent");
      } else {
        toast.error("Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setTicketReplyLoading(false);
    }
  };

  // Delete support ticket (admin only)
  const handleDeleteTicket = async (ticketId, ticketNumber) => {
    if (!window.confirm(`Are you sure you want to delete ticket ${ticketNumber || ticketId}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${BASE_URL}/api/admin/support/tickets/${ticketId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        toast.success(`Ticket ${ticketNumber || ticketId} deleted`);
        fetchSupportTickets();
        // Close detail dialog if open
        if (selectedTicket?.id === ticketId) {
          setTicketDetailOpen(false);
          setSelectedTicket(null);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete ticket");
      }
    } catch (error) {
      toast.error("Failed to delete ticket");
    }
  };

  // Load support data when tab is selected
  useEffect(() => {
    if (activeTab === "support") {
      fetchSupportTickets();
      fetchReplyTemplates();
    }
  }, [activeTab, fetchSupportTickets, fetchReplyTemplates]);

  useEffect(() => {
    const loadData = async () => {
      await fetchStats();
      await Promise.all([fetchUsers(), fetchVideos()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchUsers, fetchVideos]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchUsers]);

  useEffect(() => {
    fetchVideos();
  }, [videoFilter, fetchVideos]);

  // Fetch featured videos when Videos tab is active
  useEffect(() => {
    if (activeTab === "videos") {
      fetchFeaturedVideos();
    }
  }, [activeTab, fetchFeaturedVideos]);

  const handleToggleUserStatus = async (user, field) => {
    setActionLoading(true);
    try {
      const newValue = !user[field];
      await adminAPI.updateUser(user.id, { [field]: newValue });
      toast.success(`User ${field === "is_active" ? (newValue ? "activated" : "deactivated") : (newValue ? "promoted to admin" : "demoted from admin")}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    setActionLoading(true);
    try {
      await adminAPI.deleteVideo(videoId);
      toast.success("Video deleted successfully");
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete video");
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(true);
    try {
      const res = await adminAPI.deleteUser(userId);
      toast.success(res.data.message || "User deleted successfully");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  };

  const handleSetupFirstAdmin = async () => {
    try {
      await adminAPI.setupFirstAdmin();
      toast.success("You are now an admin!");
      setIsAdmin(true);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to setup admin");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // View video details for support
  const handleViewVideoDetail = async (videoId) => {
    setVideoLoading(true);
    setVideoViewerOpen(true);
    try {
      const response = await adminAPI.getVideoDetail(videoId);
      const data = response.data || response;
      setViewingVideo(data);
    } catch (error) {
      console.error("Error fetching video detail:", error);
      toast.error("Failed to load video details");
      setVideoViewerOpen(false);
    } finally {
      setVideoLoading(false);
    }
  };

  // Lookup user videos by email
  const handleUserVideosLookup = async (email) => {
    if (!email) {
      toast.error("Please enter a user email");
      return;
    }
    setUserVideosLoading(true);
    setUserVideosOpen(true);
    setUserEmailSearch(email); // Set the email in search field
    try {
      const response = await adminAPI.getUserVideos(email);
      // Handle both direct data and response.data patterns
      const data = response.data || response;
      setUserVideosData(data);
    } catch (error) {
      console.error("Error fetching user videos:", error);
      toast.error(error.response?.data?.detail || "Failed to load user videos");
      setUserVideosOpen(false);
    } finally {
      setUserVideosLoading(false);
    }
  };

  // View user profile details
  const handleViewUserProfile = async (userId) => {
    setSelectedUserForProfile(userId);
    setUserProfileOpen(true);
  };
  
  // Open user profile by email (looks up user ID first)
  const handleViewUserProfileByEmail = async (email) => {
    if (!email) return;
    try {
      // First try to find user in already loaded users
      const existingUser = users.find(u => u.email === email);
      if (existingUser) {
        setSelectedUserForProfile(existingUser.id || existingUser._id);
        setUserProfileOpen(true);
        return;
      }
      
      // Otherwise fetch user by email
      const response = await fetch(`${BASE_URL}/api/admin/users?search=${encodeURIComponent(email)}&limit=1`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setSelectedUserForProfile(data[0].id || data[0]._id);
          setUserProfileOpen(true);
        } else {
          toast.error(`User not found: ${email}`);
        }
      }
    } catch (error) {
      console.error("Error looking up user:", error);
      toast.error("Failed to load user profile");
    }
  };
  
  // State for new user profile modal
  const [selectedUserForProfile, setSelectedUserForProfile] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not admin - show setup option
  if (!isAdmin) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-lg mx-auto mt-20"
      >
        <Card className="bg-card border-border/50">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FF0055]/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#FF0055]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
            <p className="text-muted-foreground mb-6">
              You need admin privileges to access this page.
            </p>
            <div className="space-y-3">
              <Button
                onClick={handleSetupFirstAdmin}
                className="w-full bg-gradient-to-r from-[#FF0055] to-[#00F0FF] hover:opacity-90"
                data-testid="setup-first-admin-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Setup as First Admin
              </Button>
              <p className="text-xs text-muted-foreground">
                Only works if no admin exists yet
              </p>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full border-border/50"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="admin-dashboard"
    >
      <StorageWarningBanner />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-[#00F0FF]" />
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            System overview and user management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate("/admin/pre-launch")}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Pre-Launch Checklist
          </Button>
          <Button
            onClick={() => navigate("/admin/staff")}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white"
          >
            <Shield className="w-4 h-4 mr-2" />
            Staff Management
          </Button>
          <Button
            onClick={() => navigate("/admin/account-managers")}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white"
          >
            <Users className="w-4 h-4 mr-2" />
            Account Managers
          </Button>
          <Button
            onClick={() => navigate("/admin/trials")}
            className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white"
          >
            <Crown className="w-4 h-4 mr-2" />
            Trial Management
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              fetchStats();
              fetchUsers();
              fetchVideos();
              toast.success("Data refreshed");
            }}
            className="border-border/50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div variants={itemVariants}>
            <Card 
              className="bg-card border-border/50 cursor-pointer hover:border-[#00F0FF]/50 transition-colors"
              onClick={() => setActiveTab("users")}
              data-testid="stats-users-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5 text-[#00F0FF]" />
                  <Badge variant="outline" className="text-xs">
                    {stats.active_users} active
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.total_users)}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card 
              className="bg-card border-border/50 cursor-pointer hover:border-[#FF0055]/50 transition-colors"
              onClick={() => setActiveTab("videos")}
              data-testid="stats-videos-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Film className="w-5 h-5 text-[#FF0055]" />
                  <Badge variant="outline" className="text-xs text-green-400">
                    +{stats.videos_today} today
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.total_videos)}</p>
                <p className="text-xs text-muted-foreground">Total Videos</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card 
              className="bg-card border-border/50 cursor-pointer hover:border-[#00FF94]/50 transition-colors"
              onClick={() => setActiveTab("videos")}
              data-testid="stats-storage-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <HardDrive className="w-5 h-5 text-[#00FF94]" />
                </div>
                <p className="text-2xl font-bold text-white">{stats.storage_used_mb.toFixed(1)} MB</p>
                <p className="text-xs text-muted-foreground">Storage Used</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card 
              className="bg-card border-border/50 cursor-pointer hover:border-purple-400/50 transition-colors"
              onClick={() => setActiveTab("videos")}
              data-testid="stats-weekly-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-2xl font-bold text-white">{stats.videos_this_week}</p>
                <p className="text-xs text-muted-foreground">Videos This Week</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Video Status Breakdown */}
      {stats && (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-white">Video Status Breakdown</CardTitle>
              <CardDescription>Click any status to filter videos</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.videos_by_status).map(([status, count]) => {
                  const StatusIcon = statusIcons[status] || Activity;
                  return (
                    <div
                      key={status}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${statusColors[status] || "bg-secondary/30"}`}
                      onClick={() => setActiveTab("videos")}
                      data-testid={`status-filter-${status}`}
                    >
                      <StatusIcon className="w-4 h-4" />
                      <span className="capitalize font-medium">{status}</span>
                      <Badge variant="secondary" className="ml-1">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabs for Users and Videos */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-secondary/50 inline-flex min-w-max md:w-full">
            <TabsTrigger value="users" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span> ({users.length})
            </TabsTrigger>
            <TabsTrigger value="videos" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
              <Film className="w-4 h-4" />
              <span className="hidden sm:inline">Videos</span> ({videos.length})
            </TabsTrigger>
            <TabsTrigger value="top-creators" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap">
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">Top</span> Creators
            </TabsTrigger>
            <TabsTrigger value="referrals" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap" onClick={fetchReferralData}>
              <Gift className="w-4 h-4" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="activity-logs" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap" onClick={fetchActivityLogs}>
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Activity</span>
              {activityLogStats?.unresolved?.total > 0 && (
                <Badge variant="destructive" className="ml-1 text-[10px] px-1.5 py-0">
                  {activityLogStats.unresolved.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="financials" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap" onClick={fetchFinancialsData}>
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Financials</span>
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap" onClick={() => fetchManualSubscriptions()}>
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Subscriptions</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap" onClick={() => fetchSupportTickets()}>
              <Headphones className="w-4 h-4" />
              <span className="hidden sm:inline">Support</span>
              {supportStats?.open > 0 && (
                <Badge variant="destructive" className="ml-1 text-[10px] px-1.5 py-0">
                  {supportStats.open}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-secondary/50 border-border/50"
                data-testid="admin-user-search"
              />
            </div>
          </div>

          <Card className="bg-card border-border/50">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead>User</TableHead>
                    <TableHead>Videos</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow 
                      key={user.id || user._id} 
                      className="border-border/50 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => handleViewUserProfile(user.id || user._id)}
                      data-testid={`user-row-${user.id || user._id}`}
                    >
                      <TableCell>
                        <button 
                          onClick={() => handleViewUserProfile(user.id || user._id)}
                          className="flex items-center gap-3 text-left hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors w-full"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {user.name?.charAt(0) || user.email?.charAt(0) || "?"}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white">{user.name || "No name"}</p>
                              {user.is_admin && (
                                <Badge className="bg-[#FF0055]/20 text-[#FF0055] border-none text-[10px]">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Admin
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-cyan-400">{user.email}</p>
                          </div>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.video_count}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(user.last_activity)}
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400 border-none">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-none">
                            <UserX className="w-3 h-3 mr-1" />
                            Disabled
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewUserProfile(user.id || user._id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUserVideosLookup(user.email)}>
                              <Film className="w-4 h-4 mr-2" />
                              View Videos
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setConfirmDialog({
                                type: "toggle_active",
                                user,
                                title: user.is_active ? "Disable User" : "Enable User",
                                description: user.is_active
                                  ? `This will prevent ${user.email} from logging in.`
                                  : `This will allow ${user.email} to log in again.`,
                              })}
                            >
                              {user.is_active ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  Disable Account
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Enable Account
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setConfirmDialog({
                                type: "toggle_admin",
                                user,
                                title: user.is_admin ? "Remove Admin" : "Make Admin",
                                description: user.is_admin
                                  ? `Remove admin privileges from ${user.email}?`
                                  : `Grant admin privileges to ${user.email}?`,
                              })}
                            >
                              {user.is_admin ? (
                                <>
                                  <ShieldOff className="w-4 h-4 mr-2" />
                                  Remove Admin
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="w-4 h-4 mr-2" />
                                  Make Admin
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {/* Subscription & Trial Actions */}
                            <DropdownMenuItem onClick={() => openUserActionModal(user, "subscription")}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Assign Subscription
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedUserForAction(user);
                              // Quick trial grant - 7 days
                              handleGrantTrial(user, 7);
                            }}>
                              <Clock className="w-4 h-4 mr-2" />
                              Grant 7-Day Trial
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setGrantCreditsUser(user);
                              setShowGrantCreditsModal(true);
                            }}>
                              <Coins className="w-4 h-4 mr-2" />
                              Grant Credits
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                              onClick={() => setConfirmDialog({
                                type: "delete_user",
                                user,
                                title: "Delete User Permanently",
                                description: `This will PERMANENTLY delete ${user.email} and ALL their data including videos, settings, and referrals. This action cannot be undone!`,
                              })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos" className="space-y-4">
          <UgcQueueSettingsCard />
          <ActionSuccessReportCard onOpenVideo={handleViewVideoDetail} />
          <div className="flex items-center justify-between gap-4">
            <Select value={videoFilter} onValueChange={setVideoFilter}>
              <SelectTrigger className="w-40 bg-secondary/50 border-border/50">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="generating">Generating</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Quick User Video Lookup */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by user email..."
                value={userEmailSearch}
                onChange={(e) => setUserEmailSearch(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleUserVideosLookup(userEmailSearch)}
                className="w-64 bg-secondary/50 border-border/50"
                data-testid="quick-user-lookup"
              />
              <Button
                onClick={() => handleUserVideosLookup(userEmailSearch)}
                disabled={!userEmailSearch || userVideosLoading}
                variant="outline"
                className="border-border/50"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Featured Videos Section */}
          {featuredVideos.length > 0 && (
            <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    Featured Videos (Landing Page Demo)
                  </CardTitle>
                  <Badge variant="outline" className="border-yellow-500/50 text-yellow-500">
                    {featuredVideos.length} featured
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  These videos appear in the &quot;Watch Demo&quot; modal on the landing page
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {featuredVideos.map((video, index) => (
                    <div 
                      key={video.id} 
                      className="flex-shrink-0 relative group"
                    >
                      <div className="w-32 h-20 rounded-lg bg-black overflow-hidden border-2 border-yellow-500/50">
                        {video.thumbnail_url ? (
                          <img
                            src={getAssetUrl(video.thumbnail_url)}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-secondary">
                            <Film className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        {/* Order badge */}
                        <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-yellow-500 text-black text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </div>
                        {/* Remove button */}
                        <button
                          onClick={() => handleToggleFeature(video.id, true)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                          title="Remove from featured"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                      <p className="text-xs text-center mt-1 truncate w-32 text-muted-foreground">
                        {video.title?.slice(0, 20)}...
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-card border-border/50">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead>Video</TableHead>
                    <TableHead>Creator</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {videos.map((video) => {
                    const StatusIcon = statusIcons[video.status] || Activity;
                    return (
                      <TableRow 
                        key={video.id || video._id} 
                        className="border-border/50 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => handleViewVideoDetail(video.id || video._id)}
                        data-testid={`video-row-${video.id || video._id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                              {video.thumbnail_url ? (
                                <img
                                  src={getAssetUrl(video.thumbnail_url)}
                                  alt={video.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Film className="w-5 h-5 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1">
                                {video.is_featured && (
                                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                                )}
                                <p className="font-medium text-white truncate max-w-[200px]">{video.title}</p>
                              </div>
                              <p className="text-xs text-muted-foreground">{(video.id || video._id || 'N/A').toString().slice(0, 8)}...</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm text-white">{video.user_name}</p>
                            <p className="text-xs text-muted-foreground">{video.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[video.status] || "bg-secondary"}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {video.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(video.created_at)}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {video.video_url && (
                                <DropdownMenuItem
                                  onClick={() => handleViewVideoDetail(video.id || video._id)}
                                  data-testid={`view-video-${video.id || video._id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  setUserEmailSearch(video.user_email);
                                  handleUserVideosLookup(video.user_email);
                                }}
                              >
                                <Film className="w-4 h-4 mr-2" />
                                View All User Videos
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {/* Feature/Unfeature for Demo */}
                              <DropdownMenuItem
                                onClick={() => handleToggleFeature(video.id || video._id, video.is_featured)}
                                disabled={featuringVideoId === (video.id || video._id)}
                              >
                                {video.is_featured ? (
                                  <>
                                    <StarOff className="w-4 h-4 mr-2 text-yellow-500" />
                                    Remove from Demo
                                  </>
                                ) : (
                                  <>
                                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                                    Feature in Demo
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setConfirmDialog({
                                  type: "delete_video",
                                  video,
                                  title: "Delete Video",
                                  description: `This will permanently delete "${video.title}" and all associated files.`,
                                })}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Video
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Creators Tab */}
        <TabsContent value="top-creators" className="space-y-4">
          {stats?.top_creators && (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {stats.top_creators.map((creator, index) => (
                <motion.div key={creator.user_id} variants={itemVariants}>
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          index === 0 ? "bg-yellow-500" : index === 1 ? "bg-gray-400" : index === 2 ? "bg-amber-600" : "bg-secondary"
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{creator.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{creator.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#00F0FF]">{creator.video_count}</p>
                          <p className="text-xs text-muted-foreground">videos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals" className="space-y-4">
          {referralLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
            </div>
          ) : referralStats ? (
            <div className="space-y-6">
              {/* Clickable Referral Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                  className={`bg-card border-border/50 cursor-pointer transition-all hover:border-cyan-500/50 ${referralViewFilter === 'referrers' ? 'border-cyan-500 ring-1 ring-cyan-500/30' : ''}`}
                  onClick={() => { setReferralViewFilter('referrers'); fetchAllReferrers(); }}
                  data-testid="referrers-stat-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="w-5 h-5 text-cyan-400" />
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold text-white">{allReferrers.length || referralStats.total_referrers || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Referrers</p>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`bg-card border-border/50 cursor-pointer transition-all hover:border-green-500/50 ${referralViewFilter === 'overview' ? 'border-green-500 ring-1 ring-green-500/30' : ''}`}
                  onClick={() => setReferralViewFilter('overview')}
                  data-testid="conversions-stat-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold text-white">{referralStats.total_conversions || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Conversions</p>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`bg-card border-border/50 cursor-pointer transition-all hover:border-purple-500/50 ${referralViewFilter === 'unassigned' ? 'border-purple-500 ring-1 ring-purple-500/30' : ''}`}
                  onClick={() => { setReferralViewFilter('unassigned'); fetchUsersWithoutReferrer(); }}
                  data-testid="unassigned-stat-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <UserX className="w-5 h-5 text-purple-400" />
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold text-white">{usersWithoutReferrer.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Unassigned Users</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-card border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">£{referralStats.total_commission_paid || 0}</p>
                    <p className="text-xs text-muted-foreground">Commission Paid</p>
                  </CardContent>
                </Card>
              </div>

              {/* View Filter Tabs */}
              <div className="flex gap-2 border-b border-border/50 pb-2">
                <Button
                  variant={referralViewFilter === 'overview' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setReferralViewFilter('overview')}
                  className={referralViewFilter === 'overview' ? 'bg-[#00F0FF] text-black' : ''}
                >
                  Overview
                </Button>
                <Button
                  variant={referralViewFilter === 'referrers' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setReferralViewFilter('referrers'); fetchAllReferrers(); }}
                  className={referralViewFilter === 'referrers' ? 'bg-[#00F0FF] text-black' : ''}
                >
                  All Referrers
                </Button>
                <Button
                  variant={referralViewFilter === 'unassigned' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setReferralViewFilter('unassigned'); fetchUsersWithoutReferrer(); }}
                  className={referralViewFilter === 'unassigned' ? 'bg-[#00F0FF] text-black' : ''}
                >
                  Unassigned Users
                </Button>
              </div>

              {/* OVERVIEW VIEW */}
              {referralViewFilter === 'overview' && (
                <>
                  {/* Pending Payouts */}
                  <Card className="bg-card border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-green-400" />
                        Pending Payout Requests
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pendingPayouts.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-border/50">
                              <TableHead>User</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Method</TableHead>
                              <TableHead>Requested</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pendingPayouts.map((payout) => (
                              <TableRow key={payout.id} className="border-border/50">
                                <TableCell>
                                  <p className="text-white font-medium">{payout.user_name || "Unknown"}</p>
                                  <p className="text-xs text-muted-foreground">{payout.user_email}</p>
                                </TableCell>
                                <TableCell>
                                  <span className="text-green-400 font-bold">£{payout.amount}</span>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="capitalize">{payout.method}</Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {formatDate(payout.requested_at)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex gap-2 justify-end">
                                    <Button
                                      size="sm"
                                      className="bg-green-500 hover:bg-green-600"
                                      onClick={async () => {
                                        try {
                                          await adminAPI.processPayout(payout.id, "approve");
                                          toast.success("Payout approved");
                                          fetchReferralData();
                                        } catch (e) {
                                          toast.error("Failed to approve payout");
                                        }
                                      }}
                                    >
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={async () => {
                                        try {
                                          await adminAPI.processPayout(payout.id, "reject", "Admin rejected");
                                          toast.success("Payout rejected");
                                          fetchReferralData();
                                        } catch (e) {
                                          toast.error("Failed to reject payout");
                                        }
                                      }}
                                    >
                                      <XCircle className="w-3 h-3 mr-1" />
                                      Reject
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No pending payout requests</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top Referrers Leaderboard */}
                  {referralStats.top_referrers && referralStats.top_referrers.length > 0 && (
                    <Card className="bg-card border-border/50">
                      <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                          <Crown className="w-5 h-5 text-yellow-400" />
                          Top Referrers
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {referralStats.top_referrers.map((referrer, index) => (
                            <div
                              key={referrer.user_id}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors ${
                                index === 0 ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-secondary/30"
                              }`}
                              onClick={() => fetchReferrerDetails(referrer.user_id)}
                            >
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                index === 0 ? "bg-yellow-500 text-black" :
                                index === 1 ? "bg-slate-400 text-black" :
                                index === 2 ? "bg-amber-700 text-white" :
                                "bg-white/10 text-white/60"
                              }`}>
                                {index + 1}
                              </span>
                              <div className="flex-1">
                                <p className="text-white font-medium">{referrer.name || "Anonymous"}</p>
                                <p className="text-muted-foreground text-xs">{referrer.email}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-white font-bold">{referrer.referral_count} referrals</p>
                                <p className="text-green-400 text-sm">£{referrer.total_commission || 0} earned</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* ALL REFERRERS VIEW */}
              {referralViewFilter === 'referrers' && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Users className="w-5 h-5 text-cyan-400" />
                        All Referrers ({allReferrers.length})
                      </CardTitle>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Search by name, email, or code..."
                          value={referrerSearch}
                          onChange={(e) => setReferrerSearch(e.target.value)}
                          className="w-64 bg-secondary/50 border-border/50"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchAllReferrers(referrerSearch)}
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription>Click on a referrer to view their referred users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {referrersLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
                      </div>
                    ) : allReferrers.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border/50">
                            <TableHead>User</TableHead>
                            <TableHead>Referral Code</TableHead>
                            <TableHead className="text-center">Referrals</TableHead>
                            <TableHead>Last Referral</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allReferrers.map((referrer) => (
                            <TableRow 
                              key={referrer.id} 
                              className="border-border/50 cursor-pointer hover:bg-secondary/30"
                              onClick={() => fetchReferrerDetails(referrer.id)}
                            >
                              <TableCell>
                                <p className="text-white font-medium">{referrer.name || "No name"}</p>
                                <p className="text-xs text-muted-foreground">{referrer.email}</p>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono">{referrer.referral_code}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-2xl font-bold text-[#00F0FF]">{referrer.referral_count}</span>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {referrer.last_referral_date ? formatDate(referrer.last_referral_date) : "N/A"}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                                  onClick={(e) => { e.stopPropagation(); fetchReferrerDetails(referrer.id); }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No referrers found</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* UNASSIGNED USERS VIEW (with Bulk Assignment) */}
              {referralViewFilter === 'unassigned' && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <UserX className="w-5 h-5 text-purple-400" />
                        Users Without Referrer ({usersWithoutReferrer.length})
                      </CardTitle>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Search..."
                          value={searchUnaffiliatedUsers}
                          onChange={(e) => setSearchUnaffiliatedUsers(e.target.value)}
                          className="w-48 bg-secondary/50 border-border/50"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchUsersWithoutReferrer(searchUnaffiliatedUsers)}
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription>Select multiple users and assign them to a referrer</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Bulk Assignment Bar */}
                    {selectedUsersForBulk.length > 0 && (
                      <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <span className="text-white font-medium">{selectedUsersForBulk.length} selected</span>
                        <Input
                          placeholder="Enter referrer code..."
                          value={bulkReferrerCode}
                          onChange={(e) => setBulkReferrerCode(e.target.value.toUpperCase())}
                          className="w-48 bg-secondary/50 border-border/50"
                        />
                        <Button
                          onClick={handleBulkAssignReferrals}
                          disabled={bulkAssignLoading || !bulkReferrerCode}
                          className="bg-purple-500 hover:bg-purple-600"
                        >
                          {bulkAssignLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <UserCheck className="w-4 h-4 mr-2" />
                          )}
                          Assign All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearSelection}
                          className="text-muted-foreground"
                        >
                          Clear
                        </Button>
                      </div>
                    )}

                    {/* Select All / Actions */}
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllUnassigned}
                        disabled={usersWithoutReferrer.length === 0}
                      >
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Select All
                      </Button>
                      <p className="text-sm text-muted-foreground">
                        Click checkboxes to select users for bulk assignment
                      </p>
                    </div>

                    {/* Users Table */}
                    {usersWithoutReferrer.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border/50">
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedUsersForBulk.length === usersWithoutReferrer.length && usersWithoutReferrer.length > 0}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    selectAllUnassigned();
                                  } else {
                                    clearSelection();
                                  }
                                }}
                              />
                            </TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Signed Up</TableHead>
                            <TableHead className="text-right">Quick Assign</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {usersWithoutReferrer.map((user) => (
                            <TableRow key={user.id} className="border-border/50">
                              <TableCell>
                                <Checkbox
                                  checked={selectedUsersForBulk.some(u => u.id === user.id)}
                                  onCheckedChange={() => toggleUserSelection(user)}
                                />
                              </TableCell>
                              <TableCell>
                                <button 
                                  onClick={() => handleViewUserProfile(user.id)}
                                  className="text-left hover:bg-white/5 rounded p-1 -m-1 transition-colors"
                                >
                                  <p className="text-white font-medium">{user.name || "No name"}</p>
                                  <p className="text-xs text-cyan-400 hover:text-cyan-300">{user.email}</p>
                                </button>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {formatDate(user.created_at)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-400 hover:text-blue-300"
                                  onClick={() => {
                                    setAssignReferralEmail(user.email);
                                    setReferralViewFilter('overview');
                                  }}
                                >
                                  Assign
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>All users have been assigned to referrers!</p>
                      </div>
                    )}

                    {/* Single Assignment Form */}
                    <div className="pt-4 border-t border-border/50">
                      <p className="text-sm text-white font-medium mb-3">Or assign a single user:</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input
                          placeholder="User email..."
                          value={assignReferralEmail}
                          onChange={(e) => setAssignReferralEmail(e.target.value)}
                          className="bg-secondary/50 border-border/50"
                        />
                        <Input
                          placeholder="Referrer code..."
                          value={assignReferralCode}
                          onChange={(e) => setAssignReferralCode(e.target.value.toUpperCase())}
                          className="bg-secondary/50 border-border/50"
                        />
                        <Button
                          onClick={handleAssignReferral}
                          disabled={assignReferralLoading || !assignReferralEmail || !assignReferralCode}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {assignReferralLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <UserCheck className="w-4 h-4 mr-2" />
                          )}
                          Assign
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Click the tab to load referral data</p>
            </div>
          )}
        </TabsContent>

        {/* Activity Logs Tab */}
        <TabsContent value="activity-logs" className="space-y-4">
          {activityLogsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : activityLogStats ? (
            <div className="space-y-4">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                  className="bg-card border-border/50 cursor-pointer hover:border-white/30 transition-colors"
                  onClick={() => {
                    setActivitySeverityFilter("all");
                    setShowResolvedLogs(false);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-white">
                      {activityLogStats.unresolved?.total || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Unresolved</div>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-card border-border/50 border-red-500/30 cursor-pointer hover:border-red-500/60 transition-colors"
                  onClick={() => {
                    setActivitySeverityFilter("critical");
                    setShowResolvedLogs(false);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-red-400">
                      {activityLogStats.unresolved?.critical || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Critical</div>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-card border-border/50 border-orange-500/30 cursor-pointer hover:border-orange-500/60 transition-colors"
                  onClick={() => {
                    setActivitySeverityFilter("error");
                    setShowResolvedLogs(false);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-orange-400">
                      {activityLogStats.unresolved?.errors || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Errors</div>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-card border-border/50 cursor-pointer hover:border-white/30 transition-colors"
                  onClick={() => {
                    setActivitySeverityFilter("all");
                    setActivityLogFilter("all");
                  }}
                >
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-white">
                      {activityLogStats.last_24h || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Last 24h</div>
                  </CardContent>
                </Card>
              </div>

              {/* Filters */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <Select value={activityLogFilter} onValueChange={setActivityLogFilter}>
                      <SelectTrigger className="w-40 bg-secondary/30 border-white/10">
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="video_failed">Video Failed</SelectItem>
                        <SelectItem value="billing_error">Billing Error</SelectItem>
                        <SelectItem value="api_error">API Error</SelectItem>
                        <SelectItem value="new_signup">New Signup</SelectItem>
                        <SelectItem value="user_deleted">User Deleted</SelectItem>
                        <SelectItem value="system_error">System Error</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={activitySeverityFilter} onValueChange={setActivitySeverityFilter}>
                      <SelectTrigger className="w-36 bg-secondary/30 border-white/10">
                        <SelectValue placeholder="Severity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Severities</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="info">Info</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={showResolvedLogs}
                        onCheckedChange={setShowResolvedLogs}
                      />
                      <span className="text-sm text-muted-foreground">Show Resolved</span>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchActivityLogs}
                      className="ml-auto"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    
                    {activityLogs.filter(l => !l.resolved).length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResolveAllLogs(activityLogFilter !== "all" ? activityLogFilter : null)}
                        className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve All
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Logs Table */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-white/5">
                        <TableHead className="text-white/70">Type</TableHead>
                        <TableHead className="text-white/70">Severity</TableHead>
                        <TableHead className="text-white/70">Title</TableHead>
                        <TableHead className="text-white/70">Message</TableHead>
                        <TableHead className="text-white/70">User</TableHead>
                        <TableHead className="text-white/70">Time</TableHead>
                        <TableHead className="text-white/70 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activityLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                            No activity logs to display
                          </TableCell>
                        </TableRow>
                      ) : (
                        activityLogs.map((log) => (
                          <TableRow 
                            key={log.id} 
                            className="border-white/5 hover:bg-white/5 cursor-pointer"
                            onClick={() => {
                              setSelectedLog(log);
                              setLogDetailOpen(true);
                            }}
                          >
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={
                                  log.log_type === "billing_error" ? "border-red-500/50 text-red-400" :
                                  log.log_type === "video_failed" ? "border-orange-500/50 text-orange-400" :
                                  log.log_type === "new_signup" ? "border-green-500/50 text-green-400" :
                                  log.log_type === "api_error" ? "border-yellow-500/50 text-yellow-400" :
                                  "border-gray-500/50 text-gray-400"
                                }
                              >
                                {log.log_type.replace(/_/g, " ")}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={
                                  log.severity === "critical" ? "bg-red-500/20 text-red-400" :
                                  log.severity === "error" ? "bg-orange-500/20 text-orange-400" :
                                  log.severity === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                                  "bg-blue-500/20 text-blue-400"
                                }
                              >
                                {log.severity}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-white font-medium max-w-[150px] truncate">
                              {log.title}
                            </TableCell>
                            <TableCell className="text-muted-foreground max-w-[200px] truncate" title={log.message}>
                              {log.message.substring(0, 50)}...
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {log.user_email || log.user_id?.substring(0, 8) || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {new Date(log.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-2">
                                {!log.resolved && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResolveLog(log.id)}
                                    className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                {log.resolved && (
                                  <Badge variant="outline" className="text-green-400 border-green-500/30">
                                    Resolved
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Click the tab to load activity logs</p>
            </div>
          )}
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" className="space-y-6" data-testid="financials-tab">
          {financialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
            </div>
          ) : financialsOverview ? (
            <div className="space-y-6">
              {/* Revenue & Profit Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div variants={itemVariants}>
                  <Card className="bg-card border-border/50 border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        {financialsOverview.revenue.growth_percent !== 0 && (
                          <Badge 
                            variant="outline" 
                            className={financialsOverview.revenue.growth_percent > 0 
                              ? "text-green-400 border-green-500/30" 
                              : "text-red-400 border-red-500/30"
                            }
                          >
                            {financialsOverview.revenue.growth_percent > 0 ? "+" : ""}
                            {financialsOverview.revenue.growth_percent}%
                          </Badge>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-white">
                        £{financialsOverview.revenue.this_month.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">Revenue This Month</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Card className="bg-card border-border/50 border-l-4 border-l-[#00F0FF]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
                      </div>
                      <p className="text-2xl font-bold text-white">
                        £{financialsOverview.revenue.total.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">Total Revenue (All Time)</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Card className="bg-card border-border/50 border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <TrendingDown className="w-5 h-5 text-red-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">
                        £{financialsOverview.costs.this_month_gbp.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">Est. Costs This Month</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Card className={`bg-card border-border/50 border-l-4 ${
                    financialsOverview.profitability.monthly_margin_percent >= 50 
                      ? "border-l-green-500" 
                      : financialsOverview.profitability.monthly_margin_percent >= 20 
                      ? "border-l-yellow-500"
                      : "border-l-red-500"
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Percent className="w-5 h-5 text-purple-400" />
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {financialsOverview.profitability.monthly_margin_percent}%
                      </p>
                      <p className="text-xs text-muted-foreground">Gross Margin (Monthly)</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* MRR & Subscription Stats */}
              {subscriptionStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-card border-border/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-purple-400" />
                        Monthly Recurring Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-white mb-1">
                        £{subscriptionStats.mrr_estimated.toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ARR: £{subscriptionStats.arr_estimated.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-cyan-400" />
                        User Conversion
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-white mb-1">
                        {financialsOverview.users.conversion_rate_percent}%
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {financialsOverview.users.paid} paid / {financialsOverview.users.total} total
                      </p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="bg-card border-border/50 cursor-pointer hover:border-green-500/50 transition-all"
                    onClick={() => navigate("/admin/trials")}
                    data-testid="active-trials-card"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-green-400" />
                        Active Trials
                        <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">Click to manage</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-white mb-1">
                        {subscriptionStats.active_trials}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {financialsOverview.users.new_this_month} new users this month
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Plan Distribution */}
              {subscriptionStats?.plan_distribution && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Subscription Distribution</CardTitle>
                    <CardDescription>Users by plan tier</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {Object.entries(subscriptionStats.plan_distribution).map(([plan, count]) => (
                        <div 
                          key={plan} 
                          className={`p-4 rounded-lg border ${
                            plan === "pro" || plan === "enterprise" 
                              ? "bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30" 
                              : plan === "premium"
                              ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30"
                              : plan === "standard"
                              ? "bg-gradient-to-br from-green-500/20 to-teal-500/20 border-green-500/30"
                              : "bg-secondary/30 border-border/50"
                          }`}
                        >
                          <p className="text-xs text-muted-foreground uppercase mb-1">{plan}</p>
                          <p className="text-2xl font-bold text-white">{count}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Affiliate Program Summary */}
              {financialsOverview?.affiliates && (
                <Card className="bg-card border-border/50 border-l-4 border-l-amber-500">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <HandCoins className="w-5 h-5 text-amber-400" />
                        Affiliate Program
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActiveTab("referrals")}
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Manage Affiliates
                      </Button>
                    </div>
                    <CardDescription>Commission liabilities and affiliate performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      {/* Pending Payouts - Highlight if > 0 */}
                      <div className={`p-4 rounded-lg border ${
                        financialsOverview.affiliates.pending_payout_count > 0 
                          ? "bg-gradient-to-br from-red-500/20 to-orange-500/20 border-red-500/30" 
                          : "bg-secondary/30 border-border/50"
                      }`}>
                        <p className="text-xs text-muted-foreground mb-1">Pending Payouts</p>
                        <p className={`text-2xl font-bold ${
                          financialsOverview.affiliates.pending_payout_count > 0 ? "text-red-400" : "text-white"
                        }`}>
                          £{financialsOverview.affiliates.pending_payout_total}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {financialsOverview.affiliates.pending_payout_count} awaiting approval
                        </p>
                      </div>

                      {/* Total Commission Paid */}
                      <div className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                        <p className="text-xs text-muted-foreground mb-1">Commission Paid</p>
                        <p className="text-2xl font-bold text-green-400">
                          £{financialsOverview.affiliates.total_commission_paid}
                        </p>
                        <p className="text-xs text-muted-foreground">Total all-time</p>
                      </div>

                      {/* Active Affiliates */}
                      <div className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                        <p className="text-xs text-muted-foreground mb-1">Active Affiliates</p>
                        <p className="text-2xl font-bold text-white">
                          {financialsOverview.affiliates.active_affiliates}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {financialsOverview.affiliates.total_conversions} conversions
                        </p>
                      </div>

                      {/* Revenue from Referrals */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                        <p className="text-xs text-muted-foreground mb-1">Referral Revenue</p>
                        <p className="text-2xl font-bold text-green-400">
                          £{financialsOverview.affiliates.revenue_from_referrals}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {financialsOverview.affiliates.conversion_rate_percent}% conversion
                        </p>
                      </div>
                    </div>

                    {/* Adjusted Margin Note */}
                    {financialsOverview.affiliates.total_commission_paid > 0 && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-sm text-amber-400">
                          <strong>Adjusted Margin:</strong> {financialsOverview.profitability.adjusted_margin_percent}% 
                          (after £{financialsOverview.affiliates.total_commission_paid} affiliate commissions)
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Cost Breakdown */}
              {costBreakdown && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-orange-400" />
                      Cost Breakdown by Video Mode
                    </CardTitle>
                    <CardDescription>Estimated API costs based on video generation mode</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* This Month */}
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3">This Month</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(costBreakdown.this_month || {}).map(([mode, data]) => (
                            <div key={mode} className="bg-secondary/30 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground capitalize mb-1">{mode} Mode</p>
                              <p className="text-lg font-bold text-white">£{data.cost_gbp}</p>
                              <p className="text-xs text-muted-foreground">{data.count} videos</p>
                            </div>
                          ))}
                          {Object.keys(costBreakdown.this_month || {}).length === 0 && (
                            <p className="text-muted-foreground col-span-4 text-center py-4">No videos generated this month</p>
                          )}
                        </div>
                      </div>

                      {/* All Time */}
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3">All Time</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-border/50">
                              <TableHead>Mode</TableHead>
                              <TableHead className="text-right">Videos</TableHead>
                              <TableHead className="text-right">Total Cost</TableHead>
                              <TableHead className="text-right">Avg Cost/Video</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(costBreakdown.all_time || {}).map(([mode, data]) => (
                              <TableRow key={mode} className="border-border/50">
                                <TableCell className="font-medium capitalize">{mode}</TableCell>
                                <TableCell className="text-right">{data.count}</TableCell>
                                <TableCell className="text-right">£{data.cost_gbp}</TableCell>
                                <TableCell className="text-right">£{data.avg_cost_gbp}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Subscriptions */}
              {subscriptionStats?.recent_subscriptions?.length > 0 && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Recent Subscriptions</CardTitle>
                    <CardDescription>Latest paid subscription activations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/50">
                          <TableHead>User</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscriptionStats.recent_subscriptions.map((sub, idx) => (
                          <TableRow key={idx} className="border-border/50">
                            <TableCell>
                              <button 
                                onClick={() => handleViewUserProfileByEmail(sub.user_email)}
                                className="text-left hover:bg-white/5 rounded p-1 -m-1 transition-colors"
                              >
                                <p className="text-white font-medium">{sub.user_name || "Unknown"}</p>
                                <p className="text-xs text-cyan-400 hover:text-cyan-300">{sub.user_email}</p>
                              </button>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{sub.plan}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-green-400 font-medium">
                              £{sub.amount}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground text-sm">
                              {sub.paid_at ? new Date(sub.paid_at).toLocaleDateString() : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Revenue Per Plan */}
              {financialsOverview.revenue.by_plan && Object.keys(financialsOverview.revenue.by_plan).length > 0 && (
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Revenue by Plan</CardTitle>
                    <CardDescription>Total revenue breakdown by subscription tier</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(financialsOverview.revenue.by_plan).map(([plan, data]) => (
                        <div 
                          key={plan}
                          className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-4"
                        >
                          <p className="text-xs text-muted-foreground uppercase mb-1">{plan}</p>
                          <p className="text-xl font-bold text-green-400">£{data.total?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">{data.count || 0} transactions</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Click the tab to load financial data</p>
            </div>
          )}
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-6" data-testid="subscriptions-tab">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Subscription Management</h2>
              <p className="text-sm text-muted-foreground">Manually assign subscriptions to users</p>
            </div>
            <Button
              onClick={() => setShowSubscriptionModal(true)}
              className="bg-[#10B981] hover:bg-[#10B981]/80"
              data-testid="assign-subscription-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Assign Subscription
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#10B981]/20">
                    <Crown className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{subscriptionStats?.active_subscriptions || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Subscriptions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#A855F7]/20">
                    <Star className="w-5 h-5 text-[#A855F7]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{subscriptionStats?.pro_users || 0}</p>
                    <p className="text-xs text-muted-foreground">Pro Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#F59E0B]/20">
                    <Users className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{subscriptionStats?.business_users || 0}</p>
                    <p className="text-xs text-muted-foreground">Business Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <Clock className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{subscriptionStats?.expiring_soon || 0}</p>
                    <p className="text-xs text-muted-foreground">Expiring Soon</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Manual Subscriptions Table */}
          <Card className="bg-secondary/30 border-border/50">
            <CardHeader>
              <CardTitle className="text-white">Recent Manual Subscriptions</CardTitle>
              <CardDescription>Subscriptions assigned by administrators</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Assigned By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manualSubscriptions.length > 0 ? (
                    manualSubscriptions.map((sub, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-white">{sub.user_email}</TableCell>
                        <TableCell>
                          <Badge className={
                            sub.plan === "pro" ? "bg-[#A855F7]/20 text-[#A855F7]" :
                            sub.plan === "business" ? "bg-[#F59E0B]/20 text-[#F59E0B]" :
                            sub.plan === "enterprise" ? "bg-[#10B981]/20 text-[#10B981]" :
                            "bg-secondary/50"
                          }>
                            {sub.plan}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{sub.duration_months} month(s)</TableCell>
                        <TableCell className="text-muted-foreground">{sub.payment_method}</TableCell>
                        <TableCell className="text-muted-foreground">{sub.assigned_by_email || "Admin"}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {sub.assigned_at ? new Date(sub.assigned_at).toLocaleDateString() : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No manual subscriptions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Tickets Tab */}
        <TabsContent value="support" className="space-y-6" data-testid="support-tab">
          {/* Support Stats Cards */}
          {supportStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="pt-4 pb-3">
                  <div className="text-2xl font-bold text-yellow-400">{supportStats.open || 0}</div>
                  <p className="text-xs text-yellow-400/80">Open</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-500/10 border-blue-500/30">
                <CardContent className="pt-4 pb-3">
                  <div className="text-2xl font-bold text-blue-400">{supportStats.in_progress || 0}</div>
                  <p className="text-xs text-blue-400/80">In Progress</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-500/10 border-purple-500/30">
                <CardContent className="pt-4 pb-3">
                  <div className="text-2xl font-bold text-purple-400">{supportStats.waiting_customer || 0}</div>
                  <p className="text-xs text-purple-400/80">Waiting</p>
                </CardContent>
              </Card>
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="pt-4 pb-3">
                  <div className="text-2xl font-bold text-green-400">{supportStats.resolved || 0}</div>
                  <p className="text-xs text-green-400/80">Resolved</p>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="pt-4 pb-3">
                  <div className="text-2xl font-bold text-red-400">{supportStats.urgent || 0}</div>
                  <p className="text-xs text-red-400/80">Urgent</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* SLA Metrics */}
          {supportStats?.sla && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-testid="sla-metrics">
              <Card className="bg-secondary/30 border-border/50" data-testid="sla-avg-response">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Response Time</p>
                      <div className="text-xl font-bold text-white">
                        {supportStats.sla.avg_response_time_hours !== null 
                          ? `${supportStats.sla.avg_response_time_hours}h` 
                          : "N/A"}
                      </div>
                    </div>
                    <Clock className="w-8 h-8 text-blue-400/50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-secondary/30 border-border/50" data-testid="sla-compliance">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">SLA Compliance</p>
                      <div className={`text-xl font-bold ${
                        supportStats.sla.sla_compliance_rate >= 90 ? "text-green-400" :
                        supportStats.sla.sla_compliance_rate >= 70 ? "text-yellow-400" :
                        "text-red-400"
                      }`}>
                        {supportStats.sla.sla_compliance_rate !== null 
                          ? `${supportStats.sla.sla_compliance_rate}%` 
                          : "N/A"}
                      </div>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-400/50" />
                  </div>
                </CardContent>
              </Card>
              <Card className={`${supportStats.sla.breached_count > 0 ? "bg-red-500/10 border-red-500/30" : "bg-secondary/30 border-border/50"}`} data-testid="sla-breached">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">SLA Breached</p>
                      <div className={`text-xl font-bold ${supportStats.sla.breached_count > 0 ? "text-red-400" : "text-green-400"}`}>
                        {supportStats.sla.breached_count}
                      </div>
                    </div>
                    <AlertCircle className={`w-8 h-8 ${supportStats.sla.breached_count > 0 ? "text-red-400/50" : "text-green-400/50"}`} />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={supportSearch}
                onChange={(e) => setSupportSearch(e.target.value)}
                className="pl-9 bg-secondary/30 border-border/50"
                data-testid="support-search"
              />
            </div>
            <Select value={supportFilter.status || "all"} onValueChange={(v) => setSupportFilter(f => ({...f, status: v === "all" ? "" : v}))}>
              <SelectTrigger className="w-36 bg-secondary/30 border-border/50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_customer">Waiting</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={supportFilter.priority || "all"} onValueChange={(v) => setSupportFilter(f => ({...f, priority: v === "all" ? "" : v}))}>
              <SelectTrigger className="w-32 bg-secondary/30 border-border/50">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSupportTickets()}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${supportLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Tickets Table */}
          <Card className="bg-card border-border/50">
            <CardContent className="p-0">
              {supportLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#10B981]" />
                </div>
              ) : supportTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No tickets found
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead>Ticket</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supportTickets.map((ticket) => (
                      <TableRow 
                        key={ticket.id} 
                        className="border-border/50 hover:bg-secondary/30 cursor-pointer"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setTicketDetailOpen(true);
                          fetchTicketDetails(ticket.id);
                        }}
                        data-testid={`ticket-row-${ticket.id}`}
                      >
                        <TableCell>
                          <div>
                            <p className="text-sm font-mono text-muted-foreground">{ticket.ticket_number}</p>
                            <p className="text-sm text-white truncate max-w-[200px]">{ticket.subject}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm text-white">{ticket.user_name || "No name"}</p>
                            <p className="text-xs text-muted-foreground">{ticket.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {ticket.category?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            ticket.status === "open" ? "bg-yellow-500/20 text-yellow-400" :
                            ticket.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                            ticket.status === "waiting_customer" ? "bg-purple-500/20 text-purple-400" :
                            ticket.status === "resolved" ? "bg-green-500/20 text-green-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`}>
                            {ticket.status?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            ticket.priority === "urgent" ? "bg-red-500/20 text-red-400" :
                            ticket.priority === "high" ? "bg-orange-500/20 text-orange-400" :
                            ticket.priority === "medium" ? "bg-blue-500/20 text-blue-400" :
                            "bg-gray-500/20 text-gray-400"
                          }`}>
                            {ticket.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString() : "N/A"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateTicket(ticket.id, { status: "in_progress" });
                              }}>
                                Mark In Progress
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateTicket(ticket.id, { status: "waiting_customer" });
                              }}>
                                Waiting on Customer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateTicket(ticket.id, { status: "resolved" });
                              }}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                                Mark Resolved
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateTicket(ticket.id, { priority: "urgent" });
                              }} className="text-red-400">
                                Set Urgent
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTicket(ticket.id, ticket.ticket_number);
                                }} 
                                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                                data-testid={`delete-ticket-${ticket.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Ticket
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Subscription Assignment Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#10B981]" />
              Assign Subscription
            </DialogTitle>
            <DialogDescription>
              Manually assign a subscription plan to a user
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Search with Autocomplete */}
            <div className="space-y-2 relative">
              <label className="text-sm text-white">Search User</label>
              {selectedUserForAction ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30">
                  <User className="w-4 h-4 text-[#10B981]" />
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">{selectedUserForAction.name || selectedUserForAction.email}</p>
                    <p className="text-xs text-muted-foreground">{selectedUserForAction.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedUserForAction(null);
                      setSubscriptionEmail("");
                    }}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={subscriptionEmail}
                      onChange={(e) => {
                        setSubscriptionEmail(e.target.value);
                        searchUsersDebounced(e.target.value);
                      }}
                      onFocus={() => subscriptionEmail.length >= 2 && setShowUserDropdown(true)}
                      className="bg-secondary/30 border-border/50 pl-9"
                      data-testid="subscription-email-input"
                    />
                    {userSearchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  
                  {/* Autocomplete Dropdown */}
                  {showUserDropdown && userSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-card border border-border/50 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {userSearchResults.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                          className="w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center">
                            <User className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{user.name || "No name"}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                          {user.subscription_plan && (
                            <Badge className="text-xs bg-[#A855F7]/20 text-[#A855F7]">
                              {user.subscription_plan}
                            </Badge>
                          )}
                          {user.is_trial && (
                            <Badge className="text-xs bg-[#F59E0B]/20 text-[#F59E0B]">
                              Trial
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white">Plan</label>
              <Select value={subscriptionPlan} onValueChange={setSubscriptionPlan}>
                <SelectTrigger className="bg-secondary/30 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white">Duration (months)</label>
              <Select value={String(subscriptionDuration)} onValueChange={(v) => setSubscriptionDuration(Number(v))}>
                <SelectTrigger className="bg-secondary/30 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white">Payment Method</label>
              <Select value={subscriptionPaymentMethod} onValueChange={setSubscriptionPaymentMethod}>
                <SelectTrigger className="bg-secondary/30 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="promo">Promotional</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white">Notes (optional)</label>
              <Input
                placeholder="Add any notes..."
                value={subscriptionNotes}
                onChange={(e) => setSubscriptionNotes(e.target.value)}
                className="bg-secondary/30 border-border/50"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignSubscription}
              disabled={subscriptionLoading || !subscriptionEmail}
              className="bg-[#10B981] hover:bg-[#10B981]/80"
              data-testid="confirm-assign-subscription-btn"
            >
              {subscriptionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Assign Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {confirmDialog?.title}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(null)}
              className="border-border/50"
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog?.type === "toggle_active") {
                  handleToggleUserStatus(confirmDialog.user, "is_active");
                } else if (confirmDialog?.type === "toggle_admin") {
                  handleToggleUserStatus(confirmDialog.user, "is_admin");
                } else if (confirmDialog?.type === "delete_video") {
                  handleDeleteVideo(confirmDialog.video.id);
                } else if (confirmDialog?.type === "delete_user") {
                  handleDeleteUser(confirmDialog.user.id);
                }
              }}
              className={confirmDialog?.type === "delete_video" || confirmDialog?.type === "delete_user" ? "bg-red-600 hover:bg-red-700" : "bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Viewer Dialog */}
      <Dialog open={videoViewerOpen} onOpenChange={setVideoViewerOpen}>
        <DialogContent className="bg-card border-border/50 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Film className="w-5 h-5 text-[#00F0FF]" />
              Video Details
            </DialogTitle>
          </DialogHeader>
          
          {videoLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
            </div>
          ) : viewingVideo ? (
            <div className="space-y-4">
              {/* Video Player */}
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src={getVideoUrl(viewingVideo.stream_url, localStorage.getItem("auth_token"))}
                  controls
                  className="w-full h-full"
                  poster={viewingVideo.thumbnail_url ? getAssetUrl(viewingVideo.thumbnail_url) : undefined}
                />
              </div>
              
              {/* Download Button */}
              {viewingVideo.id && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                    onClick={() => {
                      // Use the download endpoint, not stream endpoint
                      const downloadUrl = `${BASE_URL}/api/videos/${viewingVideo.id}/download?token=${encodeURIComponent(localStorage.getItem("auth_token") || '')}`;
                      const link = document.createElement('a');
                      link.href = downloadUrl;
                      link.download = `${viewingVideo.title || 'video'}_${viewingVideo.id}.mp4`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success("Download started");
                    }}
                    data-testid="admin-video-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Video
                  </Button>
                </div>
              )}
              
              {/* Video Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Title</p>
                    <p className="text-white font-medium">{viewingVideo.title || "Untitled"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Video ID</p>
                    <p className="text-white font-mono text-sm">{viewingVideo.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={statusColors[viewingVideo.status] || "bg-secondary"}>
                      {viewingVideo.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-white text-sm">{formatDate(viewingVideo.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-white text-sm">{viewingVideo.duration || "N/A"}s</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Creator</p>
                    <p className="text-white font-medium">{viewingVideo.user_name}</p>
                    <p className="text-muted-foreground text-sm">{viewingVideo.user_email}</p>
                  </div>
                  {viewingVideo.user_subscription && (
                    <>
                      <div>
                        <p className="text-xs text-muted-foreground">User Plan</p>
                        <Badge variant="outline" className="text-[#00F0FF] border-[#00F0FF]/30">
                          {viewingVideo.user_subscription.plan_name}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Videos Used</p>
                        <p className="text-white text-sm">
                          {viewingVideo.user_subscription.videos_used} / {viewingVideo.user_subscription.video_limit}
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Mode</p>
                    <p className="text-white text-sm capitalize">{viewingVideo.mode || "standard"}</p>
                  </div>
                </div>
              </div>
              
              {/* Script/Description */}
              {viewingVideo.script && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Script</p>
                  <div className="bg-secondary/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-white text-sm whitespace-pre-wrap">{viewingVideo.script}</p>
                  </div>
                </div>
              )}
              
              {/* Error Message if failed */}
              {viewingVideo.status === "failed" && viewingVideo.error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-400 mb-1">Error Message</p>
                  <p className="text-red-300 text-sm">{viewingVideo.error}</p>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUserEmailSearch(viewingVideo?.user_email || "");
                handleUserVideosLookup(viewingVideo?.user_email);
                setVideoViewerOpen(false);
              }}
              className="border-border/50"
              disabled={!viewingVideo?.user_email}
            >
              <Film className="w-4 h-4 mr-2" />
              View All User Videos
            </Button>
            <Button
              onClick={() => setVideoViewerOpen(false)}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Videos Lookup Dialog */}
      <Dialog open={userVideosOpen} onOpenChange={setUserVideosOpen}>
        <DialogContent className="bg-card border-border/50 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00F0FF]" />
              User Videos Lookup
            </DialogTitle>
            <DialogDescription>
              Search and view all videos created by a specific user
            </DialogDescription>
          </DialogHeader>
          
          {/* Search Bar */}
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Enter user email..."
              value={userEmailSearch}
              onChange={(e) => setUserEmailSearch(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleUserVideosLookup(userEmailSearch)}
              className="bg-secondary/50 border-border/50"
              data-testid="user-videos-search"
            />
            <Button
              onClick={() => handleUserVideosLookup(userEmailSearch)}
              disabled={userVideosLoading}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
            >
              {userVideosLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {userVideosLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
            </div>
          ) : userVideosData ? (
            <div className="space-y-4">
              {/* User Info Card */}
              <div className="bg-secondary/30 rounded-lg p-4 border border-border/50">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-white font-medium">{userVideosData.user.name}</h3>
                    <p className="text-muted-foreground text-sm">{userVideosData.user.email}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Joined: {formatDate(userVideosData.user.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-[#00F0FF] border-[#00F0FF]/30 mb-1">
                      {userVideosData.subscription?.plan_name || "Free"}
                    </Badge>
                    <p className="text-white text-sm">
                      {userVideosData.subscription?.videos_used || 0} / {userVideosData.subscription?.video_limit || 1} videos
                    </p>
                    {userVideosData.subscription?.has_trial_grant && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mt-1">
                        Trial Active
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Videos List */}
              <div>
                <p className="text-muted-foreground text-sm mb-2">
                  {userVideosData.total} video{userVideosData.total !== 1 ? "s" : ""} found
                </p>
                
                {userVideosData.videos.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No videos found for this user</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                    {userVideosData.videos.map((video) => (
                      <div
                        key={video.id || video._id}
                        className="bg-secondary/50 rounded-lg p-3 border border-border/50 hover:border-[#00F0FF]/30 transition-colors cursor-pointer"
                        onClick={() => {
                          setUserVideosOpen(false);
                          handleViewVideoDetail(video.id || video._id);
                        }}
                        data-testid={`user-video-${video.id || video._id}`}
                      >
                        <div className="flex gap-3">
                          <div className="w-20 h-14 rounded bg-black flex-shrink-0 overflow-hidden">
                            {video.thumbnail_url ? (
                              <img
                                src={getAssetUrl(video.thumbnail_url)}
                                alt={video.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">{video.title || "Untitled"}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`${statusColors[video.status] || "bg-secondary"} text-xs`}>
                                {video.status}
                              </Badge>
                              <span className="text-muted-foreground text-xs">{video.duration || "N/A"}s</span>
                            </div>
                            <p className="text-muted-foreground text-xs mt-1">{formatDate(video.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Enter a user email to view their videos</p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setUserVideosOpen(false)}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal - Enhanced Version */}
      <UserProfileModal
        open={userProfileOpen}
        onOpenChange={setUserProfileOpen}
        userId={selectedUserForProfile}
        onUserUpdated={fetchUsers}
        onViewVideos={(email) => {
          setUserProfileOpen(false);
          handleUserVideosLookup(email);
        }}
        onManageTrial={(email) => {
          setUserProfileOpen(false);
          navigate(`/admin/trials?email=${encodeURIComponent(email)}`);
        }}
      />

      {/* Referrer Details Modal */}
      <Dialog open={showReferrerModal} onOpenChange={setShowReferrerModal}>
        <DialogContent className="max-w-2xl bg-[#0A0A0B] border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Referrer Details
            </DialogTitle>
            <DialogDescription>
              View all users referred by this referrer
            </DialogDescription>
          </DialogHeader>
          
          {referrerDetailsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
            </div>
          ) : referrerDetails ? (
            <div className="space-y-4">
              {/* Referrer Info */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-lg">{referrerDetails.referrer?.name || "No name"}</p>
                    <p className="text-muted-foreground text-sm">{referrerDetails.referrer?.email}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="font-mono text-lg px-3 py-1">
                      {referrerDetails.referrer?.referral_code}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-2"
                      onClick={() => {
                        navigator.clipboard.writeText(referrerDetails.referrer?.referral_code || "");
                        toast.success("Referral code copied!");
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-2xl font-bold text-[#00F0FF]">{referrerDetails.stats?.total_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                  </div>
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-2xl font-bold text-green-400">{referrerDetails.stats?.active_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-2xl font-bold text-amber-400">£{referrerDetails.referrer?.referral_credits || 0}</p>
                    <p className="text-xs text-muted-foreground">Credits Earned</p>
                  </div>
                </div>
              </div>

              {/* Referred Users List */}
              <div>
                <p className="text-white font-medium mb-2">Referred Users ({referrerDetails.referred_users?.length || 0})</p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {referrerDetails.referred_users?.length > 0 ? (
                    referrerDetails.referred_users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
                      >
                        <button 
                          onClick={() => {
                            setShowReferrerModal(false);
                            handleViewUserProfile(user.id);
                          }}
                          className="text-left"
                        >
                          <p className="text-white font-medium hover:text-cyan-400 transition-colors">{user.name || "No name"}</p>
                          <p className="text-cyan-400 text-xs hover:text-cyan-300">{user.email}</p>
                        </button>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            Signed up: {formatDate(user.created_at)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={user.is_active ? "success" : "secondary"} className={user.is_active ? "bg-green-500/20 text-green-400" : ""}>
                              {user.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {user.videos_created || 0} videos
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p>No referred users yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Failed to load referrer details</p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReferrerModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Log Detail Modal */}
      <Dialog open={logDetailOpen} onOpenChange={setLogDetailOpen}>
        <DialogContent className="bg-[#0a0a14] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Activity Log Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Type</label>
                  <p className="text-white font-medium">{selectedLog.log_type?.replace(/_/g, " ")}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Severity</label>
                  <Badge 
                    className={
                      selectedLog.severity === "critical" ? "bg-red-500/20 text-red-400" :
                      selectedLog.severity === "error" ? "bg-orange-500/20 text-orange-400" :
                      selectedLog.severity === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-blue-500/20 text-blue-400"
                    }
                  >
                    {selectedLog.severity}
                  </Badge>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Title</label>
                <p className="text-white font-medium">{selectedLog.title}</p>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Message</label>
                <p className="text-white bg-black/30 p-3 rounded-lg text-sm whitespace-pre-wrap break-words">
                  {selectedLog.message}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">User</label>
                  {selectedLog.user_email ? (
                    <button 
                      onClick={() => {
                        setSelectedLog(null);
                        handleViewUserProfileByEmail(selectedLog.user_email);
                      }}
                      className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                    >
                      {selectedLog.user_email}
                    </button>
                  ) : (
                    <p className="text-white">{selectedLog.user_id || "-"}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Created At</label>
                  <p className="text-white">{new Date(selectedLog.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              {selectedLog.video_id && (
                <div>
                  <label className="text-xs text-muted-foreground">Video ID</label>
                  <p className="text-white font-mono text-sm">{selectedLog.video_id}</p>
                </div>
              )}
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground">Additional Details</label>
                  <pre className="text-white bg-black/30 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <Badge variant="outline" className={selectedLog.resolved ? "text-green-400 border-green-500/30" : "text-yellow-400 border-yellow-500/30"}>
                  {selectedLog.resolved ? "Resolved" : "Unresolved"}
                </Badge>
                {!selectedLog.resolved && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleResolveLog(selectedLog.id);
                      setLogDetailOpen(false);
                    }}
                    className="text-green-400 hover:text-green-300 border-green-500/30"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Resolved
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Support Ticket Detail Modal */}
      <Dialog open={ticketDetailOpen} onOpenChange={setTicketDetailOpen}>
        <DialogContent className="bg-card border-border/50 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border/30 pb-4">
            <DialogTitle className="flex items-center gap-2 text-white">
              <Headphones className="w-5 h-5 text-[#10B981]" />
              {selectedTicket?.ticket_number}
            </DialogTitle>
            <DialogDescription className="text-lg text-white font-medium">
              {selectedTicket?.subject}
            </DialogDescription>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className={`text-xs ${
                selectedTicket?.status === "open" ? "bg-yellow-500/20 text-yellow-400" :
                selectedTicket?.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                selectedTicket?.status === "waiting_customer" ? "bg-purple-500/20 text-purple-400" :
                selectedTicket?.status === "resolved" ? "bg-green-500/20 text-green-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {selectedTicket?.status?.replace("_", " ")}
              </Badge>
              <Badge className={`text-xs ${
                selectedTicket?.priority === "urgent" ? "bg-red-500/20 text-red-400" :
                selectedTicket?.priority === "high" ? "bg-orange-500/20 text-orange-400" :
                selectedTicket?.priority === "medium" ? "bg-blue-500/20 text-blue-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {selectedTicket?.priority}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {selectedTicket?.category?.replace("_", " ")}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
              <span>From: <span className="text-white">{selectedTicket?.user_name || selectedTicket?.user_email}</span></span>
              <span>Created: {selectedTicket?.created_at ? new Date(selectedTicket.created_at).toLocaleDateString() : "N/A"}</span>
            </div>
          </DialogHeader>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
            {selectedTicket?.messages?.map((msg, idx) => (
              <div
                key={msg.id || idx}
                className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${
                  msg.sender_type === 'admin'
                    ? 'bg-[#10B981]/20 border-[#10B981]/30'
                    : 'bg-secondary/50 border-border/30'
                } border rounded-lg p-3`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium ${
                      msg.sender_type === 'admin' ? 'text-[#10B981]' : 'text-[#A855F7]'
                    }`}>
                      {msg.sender_type === 'admin' ? 'Support Team' : msg.sender_name || 'User'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-white whitespace-pre-wrap">
                    {msg.message}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="border-t border-border/30 p-4 space-y-4">
            {/* Status/Priority Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Select 
                value={selectedTicket?.status || "open"} 
                onValueChange={(v) => {
                  handleUpdateTicket(selectedTicket.id, { status: v });
                }}
                disabled={ticketUpdateLoading}
              >
                <SelectTrigger className="w-40 bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_customer">Waiting on Customer</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={selectedTicket?.priority || "medium"} 
                onValueChange={(v) => {
                  handleUpdateTicket(selectedTicket.id, { priority: v });
                }}
                disabled={ticketUpdateLoading}
              >
                <SelectTrigger className="w-32 bg-secondary/30 border-border/50">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              {/* Delete Ticket Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
                onClick={() => handleDeleteTicket(selectedTicket?.id, selectedTicket?.ticket_number)}
                data-testid="delete-ticket-detail-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
              {/* Quick Templates */}
              {replyTemplates.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Templates
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {replyTemplates.map((template) => (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => setTicketReply(template.message)}
                      >
                        {template.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* Reply Input */}
            {selectedTicket?.status !== 'closed' && (
              <div className="flex gap-2">
                <textarea
                  placeholder="Type your reply..."
                  value={ticketReply}
                  onChange={(e) => setTicketReply(e.target.value)}
                  className="flex-1 min-h-[80px] bg-secondary/30 border border-border/50 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#10B981]/50"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleTicketReply();
                    }
                  }}
                />
                <Button
                  onClick={handleTicketReply}
                  disabled={ticketReplyLoading || !ticketReply.trim()}
                  className="bg-[#10B981] hover:bg-[#10B981]/80 self-end"
                >
                  {ticketReplyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
